import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useSeo } from "../lib/seo";
import { useSite } from "../lib/site";
import Reveal from "../components/Reveal";

export default function NewsletterPage({ confirmMode = false }) {
  const { settings } = useSite();
  const [page, setPage] = useState(null);
  const [params] = useSearchParams();
  const [tokenState, setTokenState] = useState({ loading: !!confirmMode, ok: null, msg: "" });

  const [form, setForm] = useState({ email: "", name: "", consent: false, website: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  useSeo({ title: "Newsletter — Heri Sensei", description: "Berlangganan newsletter Heri Sensei: catatan tentang belajar, bertumbuh, dan kehidupan.", path: "/newsletter" });

  useEffect(() => {
    api.get("/content/newsletter").then((r) => setPage(r.data)).catch(() => setPage({}));
  }, []);

  useEffect(() => {
    if (!confirmMode) return;
    const token = params.get("token") || "";
    if (!token) {
      setTokenState({ loading: false, ok: false, msg: "Tautan konfirmasi tidak valid. Silakan daftar ulang melalui formulir di halaman ini." });
      return;
    }
    api.get(`/newsletter/confirm?token=${encodeURIComponent(token)}`, { validateStatus: () => true })
      .then((r) => setTokenState({ loading: false, ok: r.status === 200, msg: String(r.data || "") }))
      .catch(() => setTokenState({ loading: false, ok: false, msg: "Tautan konfirmasi tidak valid atau sudah kedaluwarsa." }));
  }, [confirmMode, params]);

  const c = page || {};
  const privacyNote = c.privacy_note || `Saya setuju menerima newsletter dari ${settings.brand_name || "Heri Sensei"}.`;
  const footerNote = c.footer_note || "Langganan menggunakan konfirmasi email (double opt-in). Anda dapat berhenti kapan saja melalui tautan di setiap email.";
  const set = (k) => (e) => setForm({ ...form, [k]: k === "consent" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.consent) {
      setError("Centang persetujuan untuk melanjutkan.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { email: form.email, name: form.name, consent: form.consent });
      setDone(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="newsletter-page" className="max-w-2xl mx-auto px-4 sm:px-8 py-16 md:py-24">
      {confirmMode ? (
        <div data-testid="newsletter-confirm-panel" className="text-center bg-surface border border-line rounded-xl p-10">
          {tokenState.loading ? (
            <Loader2 size={28} className="animate-spin mx-auto text-sub" />
          ) : tokenState.ok ? (
            <>
              <CheckCircle2 size={32} className="mx-auto text-moss" />
              <h1 className="font-serif text-3xl text-ink mt-5">Langganan aktif</h1>
              <p className="text-sub mt-3">{tokenState.msg}</p>
            </>
          ) : (
            <>
              <XCircle size={32} className="mx-auto text-enji" />
              <h1 className="font-serif text-3xl text-ink mt-5">Konfirmasi belum berhasil</h1>
              <p className="text-sub mt-3">{tokenState.msg}</p>
            </>
          )}
        </div>
      ) : (
        <Reveal>
          <p className="text-xs uppercase tracking-[0.22em] font-semibold text-enji">{c.eyebrow || "Newsletter"}</p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-4 leading-tight text-ink" data-testid="newsletter-page-title">
            {c.title || "Subscribe to My Newsletter"}
          </h1>
          <p className="text-sub text-lg leading-relaxed mt-5">
            {c.subtext || "Catatan tentang belajar, bertumbuh, dan melihat kehidupan dari sudut pandang yang lebih luas. Langsung ke email Anda."}
          </p>
          {c.frequency_note && <p className="text-faint text-sm mt-3">{c.frequency_note}</p>}

          {done ? (
            <div data-testid="newsletter-page-success" className="mt-8 flex gap-3 bg-moss/10 border border-moss/30 rounded-lg p-5">
              <CheckCircle2 className="text-moss shrink-0 mt-0.5" size={20} />
              <p className="text-ink text-[15px] leading-relaxed">{done.message}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="newsletter-page-form">
              <div>
                <label htmlFor="nl-name" className="field-label">Nama (opsional)</label>
                <input id="nl-name" data-testid="newsletter-page-name-input" value={form.name} onChange={set("name")} className="field-input" placeholder="Nama Anda" />
              </div>
              <div>
                <label htmlFor="nl-email" className="field-label">Email *</label>
                <input id="nl-email" data-testid="newsletter-page-email-input" type="email" required value={form.email} onChange={set("email")} className="field-input" placeholder="nama@email.com" />
              </div>
              <label className="flex items-start gap-2.5 text-sm text-sub cursor-pointer">
                <input data-testid="newsletter-page-consent-checkbox" type="checkbox" checked={form.consent} onChange={set("consent")} required className="mt-0.5 w-4 h-4 accent-[var(--enji)]" />
                <span>{privacyNote} <a href="/privasi" className="underline underline-offset-2">Kebijakan Privasi</a></span>
              </label>
              {error && <p data-testid="newsletter-page-error" role="alert" className="text-sm text-enji">{error}</p>}
              <button type="submit" data-testid="newsletter-page-submit-button" disabled={loading} className="btn-primary w-full">
                {loading && <Loader2 size={16} className="animate-spin" />}
                {c.cta_label || "Berlangganan"}
              </button>
            </form>
          )}
          <p className="text-[13px] text-faint mt-6 leading-relaxed">{footerNote}</p>
        </Reveal>
      )}
    </div>
  );
}
